/**
 * LeadMapper
 * 
 * Responsabilidade: Conversão entre entidades de domínio (Lead) e estrutura do banco (OpportunityDbRow)
 * Princípio: Single Responsibility - apenas mapeamento de dados
 */

import { Lead } from '../../../domain/entities/Lead';

/**
 * Sanitiza email para garantir formato válido ou string vazia
 * Evita erros de validação quando dados do banco estão corrompidos
 */
const sanitizeEmail = (email: string | null | undefined): string => {
    if (!email || typeof email !== 'string') return '';
    const trimmed = email.trim();
    if (trimmed === '') return '';
    // Regex básico para validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(trimmed) ? trimmed : '';
};

/**
 * Tipo para dados brutos do banco de dados
 */
export interface OpportunityDbRow {
    id: string;
    title: string;
    value: number;
    stage: string;
    origin: string;
    assigned_to: string | null;
    tags: string[];
    observations: string;
    contact_id: string | null;
    organization_id: string;
    created_at: string;
    stage_entry_date: string | null;
    campaign: string | null;
    ad_set: string | null;
    ad_content: string | null;
    // Campos de status e ganho/perda
    status: 'open' | 'won' | 'lost';
    won_at: string | null;
    lost_at: string | null;
    lost_reason_id: string | null;
    loss_comments: string | null;
    created_by: string | null;
    contact?: {
        name: string;
        email: string | null;
        whatsapp: string | null;
        phone: string | null;
    };
    creator?: {
        id: string;
        full_name: string | null;
    } | null;
}

export class LeadMapper {
    /**
     * Mapeia dados do banco para entidade de domínio
     */
    static toEntity(data: any): Lead {
        // Sanitização: garante valores padrão seguros
        const title = data.title || data.contact?.name || 'Nova Oportunidade';
        const contact = data.contact || {};

        return new Lead(
            data.id,
            title,
            sanitizeEmail(contact.email),
            contact.whatsapp || contact.phone || '',
            data.value || 0,
            data.stage || '',
            data.origin || 'Outros',
            data.status || 'open',
            data.assigned_to || null,
            data.tags || [],
            data.observations || '',
            new Date(data.created_at),
            data.contact_id || undefined,
            data.created_by || undefined,
            LeadMapper.getCreatorName(data),
            data.stage_entry_date ? new Date(data.stage_entry_date) : undefined,
            data.won_at ? new Date(data.won_at) : undefined,
            data.lost_at ? new Date(data.lost_at) : undefined,
            data.lost_reason_id || undefined,
            data.loss_comments || undefined
        );
    }

    /** Nome do criador a partir do join profiles (somente leitura/UI). */
    static getCreatorName(data: { creator?: { full_name?: string | null } | null }): string | undefined {
        const name = data.creator?.full_name?.trim();
        return name || undefined;
    }

    /**
     * Mapeia entidade de domínio para formato do banco
     * @param isNew - Se true, remove tags para evitar erro de duplicate key na criação
     */
    static toDatabase(lead: Lead, isNew = false): any {
        // Sanitiza agent: deve ser string ou null, nunca array
        let sanitizedAgent: string | null = null;
        if (lead.agent && typeof lead.agent === 'string' && lead.agent.trim() !== '') {
            sanitizedAgent = lead.agent;
        }

        const dbLead: any = {
            id: lead.id,
            title: lead.name,
            value: lead.getValue(),
            stage: lead.stage,
            origin: lead.origin,
            status: lead.status,
            assigned_to: sanitizedAgent,
            observations: lead.observations,
            contact_id: lead.contactId || null,
            stage_entry_date: lead.stageEntryDate ? lead.stageEntryDate.toISOString() : null,
            // Campos de ganho/perda
            won_at: lead.wonAt || null,
            lost_at: lead.lostAt || null,
            lost_reason_id: lead.lostReasonId || null,
            loss_comments: lead.lossComments || null
        };

        if (Array.isArray(lead.tags)) {
            dbLead.tags = lead.tags;
        }

        return dbLead;
    }
}
